import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import './RevisionModal.css';

interface RevisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestId: string;
  onRevisionRequested: () => void;
}

const RevisionModal: React.FC<RevisionModalProps> = ({
  isOpen,
  onClose,
  requestId,
  onRevisionRequested
}) => {
  const { t } = useTranslation();
  const [revisionNotes, setRevisionNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { token, user } = useAuth();

  const handleSubmit = async () => {
    if (!revisionNotes.trim()) {
      setError(t('common.revision_notes_required'));
      return;
    }

    if (!token) {
      setError('Authentication required. Please login again.');
      return;
    }

    // Check if user is admin
    if (user?.role !== 'admin') {
      setError(t('common.only_admins_can_request_revisions'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.REVISION_REQUEST(requestId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          revision_notes: revisionNotes.trim()
        })
      });

      const result = await response.json();

      if (result.success) {
        onRevisionRequested();
        onClose();
        setRevisionNotes('');
      } else {
        setError(result.message || t('common.failed_request_revision'));
      }
    } catch (error) {
      console.error('Error requesting revision:', error);
              setError(t('common.failed_request_revision') + '. ' + t('common.please_try_again'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRevisionNotes('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="revision-modal-overlay" onClick={handleClose}>
      <div className="revision-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="revision-modal-header">
          <h2>{t('components.revision_modal.request_revision')}</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="revision-modal-body">
          <div className="revision-info">
            <p>{t('components.revision_modal.revision_info')}</p>
            <p>{t('components.revision_modal.revision_info_2')}</p>
          </div>

          <div className="form-group">
            <label htmlFor="revision-notes">{t('components.revision_modal.revision_notes')}</label>
            <textarea
              id="revision-notes"
              value={revisionNotes}
              onChange={(e) => setRevisionNotes(e.target.value)}
              placeholder={t('components.revision_modal.revision_notes_placeholder')}
              rows={6}
              className="revision-notes-input"
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="revision-modal-footer">
          <button
            className="cancel-btn"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('components.revision_modal.cancel')}
          </button>
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting || !revisionNotes.trim()}
          >
            {isSubmitting ? t('components.revision_modal.requesting') : t('components.revision_modal.request_revision')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevisionModal; 